import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ActiveToast } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Store
import { ProfileActions, profileQuery } from '@amfam/profile/data-access';
import { LogoModel } from '@amfam/shared/models';
import { userQuery } from '@amfam/shared/user';
import { BrandSelectors } from '@amfam/shared/utility/brand';
import { EventDispatcherService } from '@amfam/shared/utility/shared-services';
import { DockingBarService, ToasterService } from '@amfam/ui-kit';

import * as fromRoot from '../core/store';

@Component({
  selector: 'app-account',
  templateUrl: './account.component.html',
  styleUrls: ['./account.component.scss'],
  standalone: false
})
export class AccountComponent implements OnInit, OnDestroy {
  // These are wired here so they persist across the entire account module instead of being reinstantiated on every click.
  showBreadcrumb = true;
  showGradient = false;
  chatEnabled$: Observable<boolean>;
  isPartner$: Observable<boolean>;
  partnerFooterLogo$: Observable<LogoModel>;
  lastLogin$: Observable<string>;
  activeToast: ActiveToast<any>;
  showBlueBreadCrumbBackground = false;

  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private dockingService: DockingBarService,
    private toasterService: ToasterService,
    private route: ActivatedRoute,
    private router: Router,
    public eventDispatcher: EventDispatcherService
  ) {}

  ngOnDestroy(): void {
    this.store.dispatch(ProfileActions.ClearToast());
    this.stop$.next();
    this.stop$.complete();
  }

  ngOnInit() {
    this.dockingService.registerHeading('Overview');

    // manage toast messages for profile page, subscribe at top level to prevent
    // extra toast from popping if you leave profile and come back to it after getting a toast
    this.store
      .select(profileQuery.selectToastMessage)
      .pipe(takeUntil(this.stop$))
      .subscribe(({ type, title, body, hide }) => {
        if (type && !hide) {
          this.toasterService.pop(type, body, title);
          this.store.dispatch(ProfileActions.ClearToast());
        }
      });

    this.router.events.pipe(takeUntil(this.stop$)).subscribe(() => {
      const currentRoute = this.route.snapshot.firstChild?.routeConfig?.path;

      if (currentRoute === 'overview') {
        this.showBreadcrumb = false;
      } else {
        this.showBreadcrumb = true;
      }
    });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.url === '/overview' || /^\/billing\/\d{11}$/.test(event.url)) {
          this.showGradient = true;
        } else {
          this.showGradient = false;
        }
      }
    });

    this.chatEnabled$ = this.store.select(fromRoot.chatEnabled);
    this.partnerFooterLogo$ = this.store.select(BrandSelectors.selectFooterLogo);
    this.lastLogin$ = this.store.select(userQuery.selectLastLoggedInOn);
  }
}
